import { faUserSecret } from "@fortawesome/pro-light-svg-icons/faUserSecret";
import { faUser } from "@fortawesome/pro-regular-svg-icons/faUser";
import { Divider } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import { AccessKeys } from "app/appConstants";
import { CorePermissions } from "app/appPermissions";
import { AccountAccessType, UserType } from "generated/CoreApi_v1";
import { useSession } from "providers/SessionProvider";
import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { RootPaths } from "routes/RootRouter";
import { AuthService } from "services/authService";
import { DirectMessageDialog } from "views/foundation/directMessage/DirectMessageDialog";
import { SpyLoginRequestDialog } from "views/foundation/user/dialogs/SpyLoginRequestDialog";
import { UserAbsenceAssistantDialog } from "views/foundation/user/dialogs/UserAbsenceAssistantDialog";
import { UserChangePasswordDialog } from "views/foundation/user/dialogs/UserChangePasswordDialog";
import { AboutDialog } from "views/public/AboutDialog";
import { HeaderLinkButton } from "./HeaderLinkButton";

/////////////////////////////////////////////////////////////////////////////////////////////////

type MenuOptions = "about" | "logout" | "logoutAll" | "view" | "edit" | "changePassword" | "absenceAssistant" | "directMessage" | "requestSpyAuth";

interface IState {
    lastUpdate: Nullable<number>;
    open: boolean;
}

/////////////////////////////////////////////////////////////////////////////////////////////////

export const HeaderUserMenu: React.FC = () => {
    const session = useSession();
    const { name } = session.user;
    const navigate = useNavigate();
    const { t } = useTranslation("shared");
    const [dialog, setDialog] = useState<React.ReactElement | null>(null);

    const [state, setState] = useState<IState>({
        lastUpdate: null,
        open: false,
    });

    ///
    function handleToggle() {
        setState({ ...state, open: !state.open });
    }

    ///
    function handleClose() {
        setState({ ...state, open: false });
    }

    ///
    function handleAction(action: MenuOptions) {

        switch (action) {
            case "about": {
                setDialog(
                    <AboutDialog onClose={() => setDialog(null)} />
                );
                break;
            }

            case "logout": {
                session.finalizeSession(() => navigate(RootPaths.Login, { replace: true }));
                break;
            }

            case "logoutAll": {
                session.finalizeSession(() => {
                    if (session.config.samlOptions?.samlLogoutUrl != null) {
                        AuthService.openSamlDialog(session.config.samlOptions?.samlLogoutUrl);
                    }
                    navigate(RootPaths.Login, { replace: true });
                });
                break;
            }

            case "view": {
                navigate(RootPaths.SelfProfile);
                break;
            }

            case "edit": {
                navigate(RootPaths.EditSelfProfile);
                break;
            }

            case "changePassword": {
                setDialog(<UserChangePasswordDialog onClose={() => setDialog(null)} />);
                break;
            }

            case "absenceAssistant": {
                setDialog(<UserAbsenceAssistantDialog
                    userId={session.user.userId}
                    name=""
                    onClose={() => setDialog(null)}
                />);
                break;
            }

            case "directMessage": {
                setDialog(<DirectMessageDialog
                    onClose={() => setDialog(null)}
                />);
                break;
            }

            case "requestSpyAuth": {
                setDialog(<SpyLoginRequestDialog
                    onClose={() => setDialog(null)}
                />);
            }
        }

        setState({ ...state, open: false });
    }

    //
    function buildMenu() {
        const ml = [];

        // -- //

        ml.push(<MenuItem key="m1" onClick={(e) => handleAction("view")}>{t("App.Menu.Topbar.User.ViewProfile")}</MenuItem>);

        ml.push(<MenuItem key="m2" onClick={(e) => handleAction("edit")}>{t("App.Menu.Topbar.User.EditProfile")}</MenuItem>);

        ml.push(<MenuItem key="m2" onClick={(e) => handleAction("absenceAssistant")}>{t("App.Menu.Topbar.User.AbsenceAssistant")}</MenuItem>);

        if (session.user.userType != UserType.Admin && session.user.accessType == AccountAccessType.Standard && session.permissionExplorer.hasAll([CorePermissions.ProfileChangePassword])) {
            ml.push(<MenuItem key="m4" onClick={(e) => handleAction("changePassword")}>{t("App.Menu.Topbar.User.ChangePassword")}...</MenuItem>);
        }

        // -- //

        ml.push(<Divider key="divMessages" />);
        ml.push(<MenuItem key="m2" onClick={(e) => handleAction("directMessage")}>{t("App.Menu.Topbar.User.DirectMessages")}</MenuItem>);
        if (session.permissionExplorer.hasAll([CorePermissions.UsersImpersonate])) {
            ml.push(<Divider key="divImpersonate" />);
            ml.push(<MenuItem key="m10" onClick={(e) => handleAction("requestSpyAuth")}>{t("App.Menu.Topbar.User.RequestSpyLoginAuthorization")}</MenuItem>);
        }

        // -- //

        ml.push(<Divider key="divAbout" />);
        ml.push(<MenuItem key="m7" onClick={(e) => handleAction("about")}>{t("App.Menu.Topbar.User.About")}...</MenuItem>);

        // -- //

        ml.push(<Divider key="divLogout" />);
        ml.push(<MenuItem key="m8" onClick={(e) => handleAction("logout")}>{t("App.Menu.Topbar.User.Logout")}</MenuItem>);
        if (session.user.accessType == AccountAccessType.Saml) {
            ml.push(<MenuItem key="m9" onClick={(e) => handleAction("logoutAll")}>{t("App.Menu.Topbar.User.LogoutAll")}</MenuItem>);
        }

        return ml;
    }

    ///
    return (
        <>
            {dialog}

            <HeaderLinkButton
                accessKey={AccessKeys.UserMenu}
                iconOrUrl={session.user.thumbnailUrl ?? faUser}
                notificationIcon={session.user.spyUserId ? faUserSecret : undefined}
                notificationIconSize="xl"
                notificationIconColor="white"
                tooltip={name}
                open={state.open}
                handleClose={handleClose}
                handleToggle={handleToggle}
            >
                <MenuList role="menu" dense>
                    {buildMenu()}
                </MenuList>
            </HeaderLinkButton>
        </>
    );
};
